<template>
  <IQOverlay
    v-bind="$attrs"
    v-on="$listeners"
    :showDocumentPreview="enhancedEnabled && previewOpen"
    @document-uploaded="handleDocumentUploaded"
    @close-document-preview="closePreview"
  >
    <template #uploadedFileCard>
      <UploadedFileCardWithViewer
        v-if="uploadedFile && enhancedEnabled"
        :fileName="uploadedFile.name"
        :fileType="uploadedFile.type"
        :isPreviewOpen="previewOpen"
        @open-viewer="openPreview"
      />
    </template>

    <template #documentPreview>
      <div class="ijdio-preview" data-testid="import-jd-document-preview">
        <div class="ijdio-preview__icon" aria-hidden="true">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
            <path
              d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M14 2v6h6M9 13h6M9 17h6"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
        <p v-if="uploadedFile" class="ijdio-preview__name" data-testid="import-jd-preview-name">
          {{ uploadedFile.name }}
        </p>
        <p class="ijdio-preview__hint" data-testid="import-jd-preview-hint">
          Document preview
        </p>
      </div>
    </template>
  </IQOverlay>
</template>

<script>
export default {
  name: "ImportJobDescriptionIQOverlay",
  inheritAttrs: false,
};
</script>

<script setup>
import { getCurrentInstance, onMounted, ref } from "vue";
import IQOverlay from "@components/IQOverlay/IQOverlay.vue";
import UploadedFileCardWithViewer from "@components/UploadedFileCardWithViewer/UploadedFileCardWithViewer.vue";
import { LD_FLAG_IDEA_MDP_585 } from "@components/constants/launchDarklyFlagKeys";

/**
 * ER-11580 — ImportJobDescriptionIQOverlay.
 *
 * Thin wrapper around the shared IQOverlay that adds the Import Job Description
 * shell/layout: a pinned uploaded-file card above an independently-scrolling
 * chat thread, plus a toggleable right-pane document preview. For every other
 * overlay `type` this is a pure pass-through (all attrs/listeners forwarded).
 *
 * The enhanced layout is gated behind the same `idea-MDP-585` flag that gates
 * the "Import Job Description" entry point (see PositionAttachedRolesTab).
 */

const { proxy } = getCurrentInstance();

const enhancedEnabled = ref(false);
const previewOpen = ref(false);
const uploadedFile = ref(null); // { name: string, type: string }

onMounted(async () => {
  // idea-MDP-585 gates the Import Job Description overlay; the enhanced
  // shell is only meaningful for that flow and is disabled when the flag is off.
  try {
    enhancedEnabled.value =
      (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_585, false)) === true;
  } catch (error) {
    enhancedEnabled.value = false;
  }
});

const handleDocumentUploaded = (payload) => {
  if (!payload?.name) return;
  // A newly uploaded document always starts with the preview pane closed.
  uploadedFile.value = { name: payload.name, type: payload.type || "" };
  previewOpen.value = false;
};

const openPreview = () => {
  if (!enhancedEnabled.value) return;
  previewOpen.value = true;
};

const closePreview = () => {
  previewOpen.value = false;
};
</script>

<style scoped>
.ijdio-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 100%;
  min-height: 160px;
  text-align: center;
}

.ijdio-preview__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: #f2edff;
  color: #7f56d9;
}

.ijdio-preview__name {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #101828;
  word-break: break-word;
}

.ijdio-preview__hint {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: #98a2b3;
}
</style>
